import appleSystem from '@capsizecss/metrics/appleSystem';
import arial700 from '@capsizecss/metrics/arial/700';
import arial400 from '@capsizecss/metrics/arial/regular';
import blinkMacSystemFont from '@capsizecss/metrics/blinkMacSystemFont';
import cantarell700 from '@capsizecss/metrics/cantarell/700';
import cantarell400 from '@capsizecss/metrics/cantarell/regular';
import firaSans600 from '@capsizecss/metrics/firaSans/600';
import firaSans400 from '@capsizecss/metrics/firaSans/regular';
import helveticaNeue700 from '@capsizecss/metrics/helveticaNeue/700';
import helveticaNeue400 from '@capsizecss/metrics/helveticaNeue/regular';
import oxygen700 from '@capsizecss/metrics/oxygen/700';
import oxygen400 from '@capsizecss/metrics/oxygen/regular';
import roboto700 from '@capsizecss/metrics/roboto/700';
import roboto400 from '@capsizecss/metrics/roboto/regular';
import segoeUI600 from '@capsizecss/metrics/segoeUI/600';
import segoeUI400 from '@capsizecss/metrics/segoeUI/regular';
import ubuntu700 from '@capsizecss/metrics/ubuntu/700';
import ubuntu400 from '@capsizecss/metrics/ubuntu/regular';

import type { IFont, IFontsConfig, TFontStackMetric } from './types';

const regularFallbackFonts = [
    appleSystem,
    blinkMacSystemFont,
    helveticaNeue400,
    segoeUI400,
    roboto400,
    oxygen400,
    firaSans400,
    ubuntu400,
    cantarell400,
    arial400,
];
const demiBoldFallbackFonts: TFontStackMetric[] = [
    appleSystem,
    blinkMacSystemFont,
    helveticaNeue700,
    segoeUI600,
    roboto700,
    oxygen700,
    firaSans600,
    ubuntu700,
    cantarell700,
    arial700,
];
const defaultFont: IFont = {
    locales: [],
    family: 'TT_Norms_Pro',
    version: '3.300',
    weights: {
        '400': 'Regular',
        '500': 'DemiBold',
        '600': 'DemiBold',
        '700': 'DemiBold',
    },
    styles: ['normal'],
    formats: ['woff2', 'woff'],
    opticalMetrics: {
        main: {
            familyName: 'TT_Norms_Pro',
            capHeight: 700,
            ascent: 950,
            descent: -330,
            lineGap: 0,
            unitsPerEm: 1000,
            xHeight: 480,
            xWidthAvg: 447,
        },
        fallbacksDefault: regularFallbackFonts,
        fallbacks: {
            '400': regularFallbackFonts,
            '500': demiBoldFallbackFonts,
            '600': demiBoldFallbackFonts,
            '700': demiBoldFallbackFonts,
        },
    },
    averageWidths: {
        '400': 447,
        '500': 460,
        '600': 460,
        '700': 460,
    },
    xHeights: {
        '400': 480,
        '500': 482,
        '600': 482,
        '700': 482,
    },
    subsets: [
        {
            name: 'BasicLatin',
            unicodeRange:
                'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD',
        },
        {
            name: '',
            unicodeRange:
                'U+0100-0130, U+0132-0137, U+0139-0151, U+0154-017E, U+018F, U+0192, U+01A0, U+01A1, U+01AF, U+01B0, U+01C4-01CE, U+01D3, U+01D4, U+01E6, U+01E7, U+01F1-01F5, U+01FA-01FF, U+0218-021B, U+021E, U+021F, U+0232, U+0233, U+0237, U+0244, U+0259, U+0289, U+02C7, U+02D8, U+02D9, U+02DB, U+02DD, U+0300-0303, U+0306, U+0307, U+0309-030C, U+0312, U+031B, U+0323, U+0326-0328, U+037E, U+0384-038A, U+038C, U+038E-03A1, U+03A3-03CE, U+0400-045F, U+0462, U+0463, U+0472-0475, U+048A-04FF, U+0510-0513, U+051A-051D, U+0524-0529, U+052E, U+052F, U+1E24-1E27, U+1E30, U+1E31, U+1E36, U+1E37, U+1E3E, U+1E3F, U+1E54, U+1E55, U+1E80-1E85, U+1E8C, U+1E8D, U+1E9E, U+1EA0-1EF9, U+2070, U+2075-2079, U+2080-2089, U+20A9, U+20AB, U+20AE, U+20B4, U+20B8-20BA, U+20BD, U+20BF, U+20C0, U+2113, U+2116, U+2117, U+2126, U+212E, U+2150-215F, U+2190, U+2192, U+2194-2199, U+2202, U+2205, U+2206, U+220F, U+2211, U+221A, U+221E, U+222B, U+2248, U+2260, U+2264, U+2265, U+2318, U+2460-2473, U+24EA-24F4, U+24FF, U+25CA, U+2776-277F, U+2E18, U+E900, U+F43A, U+F43B, U+F460-F463, U+F465-F46D, U+F46F-F473, U+F498-F49B, U+F49E, U+F49F, U+F4C6, U+F4C7, U+F4CC, U+F4CD, U+F4D2-F4D9, U+F50A-F523, U+F526-F52F, U+F532, U+F533, U+F536-F539, U+F53C-F53F, U+F6D1, U+F6D4, U+F730-F739, U+F830, U+F831, U+F836, U+F837, U+F839, U+F83A, U+F83C, U+F83D, U+F842, U+F843, U+F8FF, U+FB00-FB03, U+FB06',
        },
    ],
};

export const fontsConfig: IFontsConfig = {
    defaultFont,
    localFonts: [
        {
            ...defaultFont,
            locales: ['ru', 'pl'],
            subsets: [],
        },
    ],
    fontFamilyFallback:
        "-apple-system, BlinkMacSystemFont, 'Helvetica Neue', 'Segoe UI', Roboto, Oxygen-Sans, 'Fira Sans', 'Droid Sans', Ubuntu, Cantarell, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
};
